import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  LocalFilterOptions,
  ResetFilterOptions,
  SaveFilterParams,
} from 'src/components/organisms';
import {
  TimeframeInput,
  useGetReportsPerCategoryPerMonthGraphQuery,
} from 'src/graphql';
import * as Types from 'src/types';
import { InsightsDashboard, State } from 'src/types';
import {
  getOrderedColor,
  sortDataArrayByNumber,
  sortDataArrayByString,
} from 'src/utils';
import {
  CHART_IDS,
  trackChartGrouped,
  trackChartRedrawn,
} from 'src/utils/analytics';
import { buildLocationQueryVariables } from 'src/utils/insights/buildLocationQueryVariables';
import {
  DateFormats,
  diff,
  formatDate,
  getYear,
  parseDate,
  TimeUnit,
} from 'src/utils/time';
import { getColumnNames } from '../../EthicsOperationsDataview/helpers';
import {
  ColumnSortType,
  TableDataType,
} from '../../EthicsOperationsDataview/hooks';

export interface FormattedLineChartData {
  line: string;
  x: string;
  y: number;
  color: string;
}

interface Parameters {
  globalFilterSites: string[];
  globalFilterCountries: string[];
  selectedCaseCountryCodes: string[];
  selectedCaseRegionIds: string[];
  globalFilterTimeframe: TimeframeInput;
  organisationData: {
    sites?: string[];
    countries?: string[];
    departments?: string[];
    oldestReportSubmittedAtDate: Date;
    categories?: string[];
  };
}

export const useGetLineGraphData = ({
  globalFilterSites,
  globalFilterCountries,
  selectedCaseCountryCodes,
  selectedCaseRegionIds,
  globalFilterTimeframe,
  organisationData,
}: Parameters) => {
  const storedGlobalFilters = useSelector(
    (state: State) =>
      state.insightsGlobalFilters[InsightsDashboard.ORGANISATIONAL],
  );
  const currentGlobalLocationFilter =
    storedGlobalFilters.currentGlobalLocationFilter;
  const [selectedDepartments, setSelectedDepartments] = useState(
    organisationData.departments ?? [],
  );
  const [storedTableData, setStoredTableData] = useState<TableDataType[]>([]);
  const [sortedColumn, setSortedColumn] = useState<ColumnSortType>({
    columnName: 'Issue category name',
    direction: 'asc',
  });

  const [displayTableData, setDisplayTableData] = useState<TableDataType[]>([]);

  const [selectedCategories, setSelectedCategories] = useState(
    organisationData.categories ?? [],
  );
  const [selectedSites, setSelectedSites] = useState(globalFilterSites);
  const [selectedCountries, setSelectedCountries] = useState(
    globalFilterCountries,
  );
  const [localLocationFilter, setLocalLocationFilter] = useState(
    currentGlobalLocationFilter,
  );
  const [timeframe, setTimeframe] = useState(globalFilterTimeframe);
  const [activeCategories, setActiveCategories] = useState<
    string[] | undefined
  >(undefined);
  const [dateGroup, setDateGroup] = useState<'Month' | 'Quarter'>('Month');
  const tableColumns = [
    { label: dateGroup },
    { label: 'Year' },
    { label: 'Issue category name' },
    { label: 'Issue category count' },
  ];
  const [xAxisLabelCount, setXAxisLabelCount] = useState<number>(0);
  const [pdfIsDownloading, setPdfIsDownloading] = useState<boolean>(false);

  const queryLocationVariables = buildLocationQueryVariables({
    locationFilter: localLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: selectedSites,
    countries: selectedCountries,
    caseCountryCodes: selectedCaseCountryCodes,
    caseRegionIds: selectedCaseRegionIds,
  });
  const { data, isLoading } = useGetReportsPerCategoryPerMonthGraphQuery({
    timeframe,
    ...queryLocationVariables,
    departments: selectedDepartments,
    categories: selectedCategories,
    dateGroup: dateGroup === 'Quarter' ? 'Q YYYY' : 'Mon YYYY',
  });
  const lineChartData = data?.getReportsPerCategoryPerMonthGraph;
  const organisation = useSelector((state: Types.State) => state.organisation);
  const features = useSelector((state: Types.State) => state.features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const getTableData = () => {
    return (
      lineChartData?.data.flatMap((data) => {
        const mappedTableData = data.linePlot.map((item) => {
          return {
            cellData: [
              dateGroup === 'Month'
                ? formatDate(
                    parseDate(item.month, DateFormats.MONTH_YEAR),
                    DateFormats.MONTH_LONG,
                  )
                : `Q${formatDate(
                    parseDate(item.month, DateFormats.QUARTER_YEAR),
                    DateFormats.QUARTER,
                  )}`,
              dateGroup === 'Month'
                ? formatDate(
                    parseDate(item.month, DateFormats.MONTH_YEAR),
                    DateFormats.YEAR,
                  )
                : `${formatDate(
                    parseDate(item.month, DateFormats.QUARTER_YEAR),
                    DateFormats.YEAR,
                  )}`,
              data.category,
              item.count,
            ],
            row: `${data.category}-${item.month}`,
          };
        });
        return mappedTableData;
      }) ?? []
    );
  };

  useEffect(() => {
    const tableData = getTableData();
    if (!isEqual(tableData, storedTableData)) {
      setStoredTableData(tableData);
    }
    const labelsCount = lineChartData?.data[0].linePlot.length ?? 0;
    setXAxisLabelCount(labelsCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineChartData]);

  useEffect(() => {
    handleColumnSort(sortedColumn.columnName, sortedColumn.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedTableData]);

  const handleColumnSort = (columnName: string, direction: 'asc' | 'desc') => {
    const columnIndex = getColumnNames(tableColumns).indexOf(columnName);
    setSortedColumn({ columnName, direction });
    switch (columnName) {
      case 'Issue category name': {
        const newTableData = sortDataArrayByString(
          storedTableData,
          columnIndex,
          direction,
        );
        setDisplayTableData(newTableData);
        break;
      }
      case 'Year':
      case 'Month':
      case 'Issue category count': {
        const newTableData = sortDataArrayByNumber(
          storedTableData,
          columnIndex,
          direction,
        );
        setDisplayTableData(newTableData);
        break;
      }
      default: {
        setDisplayTableData(storedTableData);
        break;
      }
    }
  };
  useEffect(() => {
    if (lineChartData && activeCategories === undefined) {
      setActiveCategories(lineChartData.data.map((d) => d.category));
    }
  }, [lineChartData, activeCategories]);

  useEffect(() => {
    setActiveCategories(lineChartData?.data.map((d) => d.category));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeframe,
    selectedCategories,
    selectedDepartments,
    selectedSites,
    selectedCaseCountryCodes,
    selectedCaseRegionIds,
  ]);

  useEffect(() => {
    setSelectedSites(globalFilterSites);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterSites, currentGlobalLocationFilter]);

  useEffect(() => {
    setSelectedCountries(globalFilterCountries);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterCountries, currentGlobalLocationFilter]);

  useEffect(() => {
    setTimeframe(globalFilterTimeframe);
  }, [globalFilterTimeframe]);

  useEffect(() => {
    if (!selectedDepartments.length && organisationData.departments?.length) {
      setSelectedDepartments(organisationData.departments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationData.departments]);

  useEffect(() => {
    if (!selectedCategories.length && organisationData.categories?.length) {
      setSelectedCategories(organisationData.categories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationData.categories]);

  const localFilterOptions: LocalFilterOptions = {
    site: {
      options: organisationData.sites ?? [],
      selectedOptions: selectedSites,
    },
    country: {
      options: organisationData.countries ?? [],
      selectedOptions: selectedCountries,
    },
    time: {
      timeframe,
      oldestSelectableYear: getYear(
        organisationData.oldestReportSubmittedAtDate,
      ),
    },
    department: {
      options: organisationData.departments ?? [],
      selectedOptions: selectedDepartments,
    },
    category: {
      options: organisationData.categories ?? [],
      selectedOptions: selectedCategories,
    },
    selectedLocationFilter: localLocationFilter,
  };

  const saveLocalFilters = (params: SaveFilterParams) => {
    if (params.selectedSites) {
      setSelectedSites(params.selectedSites);
    }
    if (params.selectedCountries) {
      setSelectedCountries(params.selectedCountries);
    }
    if (params.selectedDepartments) {
      setSelectedDepartments(params.selectedDepartments);
    }
    if (params.selectedTimeframe) {
      setTimeframe(params.selectedTimeframe);
    }
    if (params.selectedCategories) {
      setSelectedCategories(params.selectedCategories);
    }
    if (params.selectedLocationFilter) {
      setLocalLocationFilter(params.selectedLocationFilter);
    }
  };

  const categories = useMemo(
    () => (lineChartData ? lineChartData.data.map((d) => d.category) : []),
    [lineChartData],
  );

  const categoryColors = useMemo(
    () =>
      Object.fromEntries(
        categories.sort().map((c, index) => [c, getOrderedColor(index)]),
      ),
    [categories],
  );

  const timeFrame = diff(timeframe.end, timeframe.start, TimeUnit.month);
  const timeFrameLessThanYear = timeFrame + 1 <= 12;

  const linePlotMap = (
    linePlot: { month: string; count: number }[],
    category: string,
  ) =>
    linePlot.map((d) => ({
      line: category,
      x:
        dateGroup === 'Quarter'
          ? `Q${formatDate(
              parseDate(d.month, DateFormats.QUARTER_YEAR),
              DateFormats.Q_YY,
              false,
            )}`
          : timeFrameLessThanYear
            ? formatDate(
                parseDate(d.month, DateFormats.MONTH_YEAR),
                DateFormats.MONTH,
                false,
              )
            : formatDate(
                parseDate(d.month, DateFormats.MONTH_YEAR),
                DateFormats.MM_YY,
                false,
              ),
      y: d.count,
      color: categoryColors[category],
      tooltipTitle:
        dateGroup === 'Quarter'
          ? `Q${formatDate(
              parseDate(d.month, DateFormats.QUARTER_YEAR),
              DateFormats.QUARTER_YEAR_COMMA,
              false,
            )}`
          : formatDate(
              parseDate(d.month, DateFormats.MONTH_YEAR),
              DateFormats.MONTH_YEAR_COMMA,
              false,
            ),
    }));
  const formattedLineChartDataArray = useMemo(
    () =>
      lineChartData
        ? lineChartData.data.reduce((acc, cur) => {
            const categoryData = linePlotMap(cur.linePlot, cur.category);
            return [...acc, ...categoryData];
          }, [] as FormattedLineChartData[])
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lineChartData, dateGroup, timeFrameLessThanYear, categoryColors],
  );

  const filteredLineChartData = useMemo(
    () =>
      formattedLineChartDataArray.filter((obj) =>
        activeCategories?.includes(obj.line),
      ),
    [formattedLineChartDataArray, activeCategories],
  );
  const lineChartLegend = useMemo(
    () =>
      lineChartData
        ? lineChartData.data.map((line) => ({
            text: line.category,
            color: categoryColors[line.category],
            active: activeCategories?.includes(line.category) ?? false,
          }))
        : [],
    [lineChartData, categoryColors, activeCategories],
  );
  const onLineChartLegendClick = (category: string) => {
    setActiveCategories((activeCategories) => {
      if (activeCategories === undefined) {
        return undefined;
      }

      if (organisation.organisation && segmentEnabled) {
        trackChartRedrawn(
          organisation.organisation.id,
          'organisational',
          CHART_IDS.categories_over_time,
        );
      }

      if (activeCategories.includes(category) && activeCategories.length > 1) {
        return activeCategories.filter((x) => x != category);
      }

      if (!activeCategories.includes(category)) {
        return [...activeCategories, category];
      }

      return activeCategories;
    });
  };

  const resetLocalFilters: ResetFilterOptions = {
    siteOptions: globalFilterSites,
    countryOptions: globalFilterCountries,
    departmentOptions: organisationData.departments ?? [],
    timeframe: globalFilterTimeframe,
    categoryOptions: organisationData.categories ?? [],
    globalFilterLocation: currentGlobalLocationFilter,
  };

  return {
    xTitle: lineChartData?.xTitle,
    yTitle: lineChartData?.yTitle,
    lineData: filteredLineChartData,
    timeframe,
    lineChartLegend,
    onLineChartLegendClick,
    localFilterOptions,
    saveLocalFilters,
    dataIsLoading: isLoading,
    categoriesOverTime: { lineChartData: lineChartData?.data, dateGroup },
    dateGroup,
    setDateGroup: (newGroup: 'Month' | 'Quarter') => {
      if (organisation.organisation && segmentEnabled) {
        trackChartGrouped(
          organisation.organisation.id,
          'organisational',
          CHART_IDS.categories_per_department,
          'Time',
          newGroup,
        );
      }
      setDateGroup(newGroup);
    },
    tableColumns,
    tableData: displayTableData,
    handleColumnSort,
    sortedColumn,
    xAxisLabelCount,
    pdfIsDownloading,
    setPdfIsDownloading,
    localLocationFilter,
    resetLocalFilters,
  };
};
